// Internal Imports
import { GRID, COMMON } from "../../constants";
import { GlobalUtils, ProgramUtils } from "../../utils";

// Destructuring
const { DEALTABLE_COLUMNS } = GRID;
const { getAppFlagValue } = GlobalUtils;
const { roundUpByDecimal, roundUpByNumber, roundUpByUnit } = ProgramUtils;
const { APP_FLAGS, ROUNDING_TYPE } = COMMON;
/**
 * "setFlagsToCatalogItems" func adds the following two flags inside each of the product data.
 * 1)isChecked - Acts as a toggle switch which will change when user selects the product checkbox option.
 * 2)isDisabled - Determines wheather the items is already added to dealtable or not.
 *
 * @param {ArrayOfObj} products - Holds the products JSON from the API
 * @param {array} addedCatalogIds - Holds the id's of the products which are already added to the dealtable
 */
export const setFlagsToCatalogItems = (products, addedCatalogIds) => {
  products.forEach(item => {
    item.isChecked = false;
    item.isDisabled = addedCatalogIds.includes(item.id);
  });
  return products;
};

/**
 * "updateProductCatalogFlags" func updates the isChecked/isDisabled flag of a single/multiple product data.
 * @param {number} productId(s) - Points to the product id // 15
 * @param {boolean} flag - Holds true/false value to set in flagType.
 * @param {string} flagType - Key which the flag value gets added to, like "isChecked & isDisabled".
 * @param {arrayOfObj} productCatalogs - Holds the entire product catalog list.
 */
export const updateProductCatalogFlags = (
  productIds,
  flag,
  flagType,
  productCatalogs,
) => {
  const updatedProductCatalogs = productCatalogs.map(item => {
    if (productIds.includes(item.id)) {
      if (!flagType) {
        return { ...item, isDisabled: flag, isChecked: flag };
      }
      return { ...item, [flagType]: flag };
    }
    return item;
  });
  return updatedProductCatalogs;
};

export const generateGroupDataInDealTable = (
  rowData,
  discount,
  columnName,
  fieldMapIndex,
) => {
  // const { groups = [] } = discount;
  const groups = [];
  const rowsData = JSON.parse(JSON.stringify(rowData));
  const groupDetailArray = [];
  const groupData = rowsData.reduce((r, a) => {
    r[a.rowData[fieldMapIndex[columnName]].cellData] =
      r[a.rowData[fieldMapIndex[columnName]].cellData] || [];
    r[a.rowData[fieldMapIndex[columnName]].cellData].push(a);
    return r;
  }, Object.create(null));
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(groupData)) {
    groupDetailArray.push({
      groupName: key,
      groupDataDetails: value,
    });
  }
  const netPriceFld = fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];
  console.debug(
    "generateGroupDataInDealTable",
    groupDetailArray,
    groups,
    rowData,
    columnName,
    fieldMapIndex,
    netPriceFld,
    fieldMapIndex[columnName],
  );
  groupDetailArray.forEach(groupInfo => {
    console.debug("groupData", groupInfo);
    const netPriceArray = [];
    const relatedRowIds = [];
    groupInfo.groupDataDetails.forEach(eachData => {
      netPriceArray.push(eachData.rowData[netPriceFld].cellData);
      const sumOfRows = netPriceArray.reduce((a, b) => a + b, 0);
      // eslint-disable-next-line no-shadow
      const groupData = groups.find(
        group => group.group_by_value === groupInfo.groupName,
      );
      relatedRowIds.push(eachData.rowMeta.id);
      const discountAmount =
        (sumOfRows * ((groupData && groupData.discount_percent) || 0)) / 100;
      if (!groupData) {
        const payload = {
          group_by: columnName.toUpperCase(),
          group_by_value: groupInfo.groupName,
          discount_percent: 0,
          group_total: sumOfRows,
          discountAmount: 0,
          discountedGroupTotal: sumOfRows,
          row_ids: relatedRowIds,
        };
        groups.push(payload);
      } else {
        groupData.group_total = sumOfRows;
        groupData.discountAmount = discountAmount;
        groupData.discountedGroupTotal = sumOfRows - discountAmount;
      }
      console.debug(
        "groupData",
        groups,
        sumOfRows,
        netPriceArray,
        groupInfo,
        eachData,
      );
    });
    discount.groups = groups;
    discount.groupReference = fieldMapIndex[columnName];
  });

  return discount;
};

/**
 * "generateGroupData" func adds group related info inside the  "discount" key in redux.
 * @param {arrayOfObj} rowData - Holds the current row(s) information in dealtable.
 * @param {Object} discount - Holds the discount related information of the selected dealtable.
 * @param {String} columnName - Holds the column name which determines the groupBy logic.
 * @param {Object} fieldMapIndex - Holds the column name => FLDXXX map.
 * @param {String} netPriceFld - Holds the FLDXXX of netprice.
 */
export const generateGroupData = (
  rowData,
  discount,
  columnName,
  fieldMapIndex,
) => {
  const { groups = [] } = discount;
  const groupByOptions = [
    ...new Set(rowData.map(row => row[fieldMapIndex[columnName]])),
  ];
  const netPriceFld = fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];

  groupByOptions.forEach(groupByValue => {
    const netPriceArray = rowData
      .filter(row => row[fieldMapIndex[columnName]] === groupByValue)
      .map(row => row[netPriceFld]);
    const sumOfRows = netPriceArray.reduce((a, b) => a + b, 0);
    const groupData = groups.find(
      group => group.group_by_value === groupByValue,
    );
    const discountAmount =
      (sumOfRows * ((groupData && groupData.discount_percent) || 0)) / 100;
    if (!groupData) {
      const payload = {
        group_by: columnName.toUpperCase(),
        group_by_value: groupByValue,
        discount_percent: 0,
        group_total: sumOfRows,
        discountAmount: 0,
        discountedGroupTotal: sumOfRows,
      };
      groups.push(payload);
    } else {
      groupData.group_total = sumOfRows;
      groupData.discountAmount = discountAmount;
      groupData.discountedGroupTotal = sumOfRows - discountAmount;
    }
  });

  return discount;
};

/**
 * "generateDiscountData" func is used to show the the row count and calculates (deal/group) level discounts.
 * @param {Object} groupsInfo - Holds deal table id, overall discount, groups array, etc
 * @param {ArrayOfObj} rows - Contains the row data of the particular dealtable.
 * @param {String} netPriceFld - FLDXXX key of netprice column.
 * @param {Boolean} resetDirtyFlag - Flag to set dealdiscount dirty or not.
 */

export const generateDiscountData = (
  groupsInfo,
  rows,
  fieldMapIndex,
  resetDirtyFlag,
) => {
  const netPriceFld = fieldMapIndex[DEALTABLE_COLUMNS.NET_PRICE];
  /**
   * Add the following two keys inside the group array::
   *  discountAmount = (total * discount) / 100 => Formula to Convert % to Number
   *  discountedGroupTotal = total - discountAmount
   */

  const { groups } = groupsInfo;
  if (groups && groups.length > 0) {
    groups.forEach(group => {
      const netPriceArray = rows
        .filter(
          row =>
            row[fieldMapIndex[DEALTABLE_COLUMNS[group.group_by]]] ===
            group.group_by_value,
        )
        .map(row => row[netPriceFld]);
      const groupTotal = netPriceArray.reduce((a, b) => a + b, 0);
      const discountAmount = (groupTotal * group.discount_percent) / 100;
      group.group_total = groupTotal;
      group.discountAmount = discountAmount;
      group.discountedGroupTotal = groupTotal - discountAmount;
    });
  }

  /**
   * * If groups are present then:
   * Extract all the discountedGroupTotal ( total - discount amount) from the groups array
   * and form the dealTotal.
   *
   * * Else:
   * Sum the netPrice of the rows to get the dealTotal
   *
   * Then add the following 3 keys in groupsInfo object at siblings level::
   *  dealTotal = sum of all the discountedGroupTotal inside the groups array
   *  discountAmount = (total * discount) / 100 => Formula to Convert % to Number
   *  discountedDealTotal = total - discountAmount
   */

  let dealTotal = 0;
  if (groups && groups.length > 0) {
    dealTotal = groups
      .map(group => group.discountedGroupTotal)
      .reduce((a, b) => a + b, 0);
  } else if (rows && rows.length > 0) {
    const netPrices = rows.map(row => row[netPriceFld]);
    dealTotal = netPrices.reduce((a, b) => a + b, 0);
  }

  const discountAmount = (dealTotal * groupsInfo.deal_discount) / 100;

  if (resetDirtyFlag) groupsInfo.isDealDiscountDirty = false;
  groupsInfo.dealTotal = dealTotal;
  groupsInfo.discountAmount = discountAmount;
  groupsInfo.discountedDealTotal = dealTotal - discountAmount;

  return groupsInfo;
};

/**
 * "addGroupIDIntoRow" func adds the GroupId into "dt_group_id" column for all the row(s).
 *
 * @param {ArrayOfObj} groups - Holds the groups data.
 * @param {string} fieldName - Holds the GroupBy value //"Charge Type (or) Family, etc,."
 * @param {ArrayOfObj} rows - Holds the dealtable row data.
 * @param {Object} fieldMapIndex - Holds the column name => FLDXXX map // {sku: "FLD9gZNWKHmRNBA"}
 */
export const addGroupIDIntoRow = (groups, fieldName, rows, fieldMapIndex) => {
  const groupIdHashMap = {};
  groups.forEach(group => {
    groupIdHashMap[group.group_by_value] = group.id;
    return groupIdHashMap;
  }); // Form hashmap like {One Time : 1}
  rows.forEach(row => {
    row[fieldMapIndex.dt_group_id] = groupIdHashMap[row[fieldName]];
  });
  return rows;
};

/**
 * "removeGroupIDFromRow" func removes the GroupId from "dt_group_id" column for all the row(s).
 *
 * @param {ArrayOfObj} rowData - Holds the entire rows of the dealtable
 * @param {number} groupIdFld - Holds the id's of the group
 */
export const removeGroupIDFromRow = (rowData, groupIdFld) => {
  rowData.forEach(row => {
    row[groupIdFld] = null;
    row.isDirty = true;
  });
  return rowData;
};

/**
 * "updateOverallDiscount" func does a math calculation in which the deal level discount is splitted by row count and the result value is added
 * into each of the row under "overallDiscountPercentFld" column. This is done, coz backend can trigger workflow if the condition is over ruled.
 *
 * @param {ArrayOfObj} rowData - Holds the entire rows of the dealtable
 * @param {ArrayOfObj} discount - Holds the discount information
 * @param {Object} fieldMapIndex - Holds the column name => FLDXXX map // {sku: "FLD9gZNWKHmRNBA"}
 * @param {String} additionalDiscountPercentFld - Holds the FLDXXX value of additionalDiscountPercent column
 * @param {String} discountPercentFld - Holds the FLDXXX value of discountPercent column
 * @param {String} overallDiscountPercentFld - Holds the FLDXXX value of overallDiscountPercent column
 */
export const updateOverallDiscount = (
  rowData,
  discount,
  fieldMapIndex,
  additionalDiscountPercentFld,
  discountPercentFld,
  overallDiscountPercentFld,
) => {
  const groupDiscountHashMap = {};
  const dealDiscount = 1 - discount.deal_discount / 100;
  const { groups } = discount;
  let appliedRowGroup = null;

  if (groups && groups.length > 0) {
    const groupedBy = groups[0].group_by.toLowerCase();
    appliedRowGroup = fieldMapIndex[groupedBy];
    groups.forEach(group => {
      groupDiscountHashMap[group.group_by_value] = group.discount_percent;
      return groupDiscountHashMap;
    }); // Form hashmap like {One Time : 10}
  }
  rowData.forEach(row => {
    const addtionalDiscountPercentage =
      row[fieldMapIndex[additionalDiscountPercentFld]];
    const discountedPercentage = row[fieldMapIndex[discountPercentFld]];
    let groupDiscount = 1;

    if (groups && groups.length > 0) {
      const groupDiscountAmount = groupDiscountHashMap[row[appliedRowGroup]];
      groupDiscount = 1 - groupDiscountAmount / 100;
    }

    const addDiscPercntFormula = 1 - addtionalDiscountPercentage / 100;
    const discountedPriceFormula = 1 - discountedPercentage / 100;

    const overallDiscount =
      (1 -
        addDiscPercntFormula *
          discountedPriceFormula *
          groupDiscount *
          dealDiscount) *
      100;

    row[fieldMapIndex[overallDiscountPercentFld]] = overallDiscount;
  });
  return rowData;
};

export const handleNetPriceRounding = (rowData, netPriceFld) => {
  const roundingType = getAppFlagValue(APP_FLAGS.ROUNDING_FLAG);
  if (ROUNDING_TYPE[roundingType]) {
    rowData.forEach(row => {
      switch (ROUNDING_TYPE[roundingType]) {
        case "decimal":
          row.rowData[netPriceFld].cellData = roundUpByDecimal(
            row.rowData[netPriceFld].cellData,
            1,
          );
          break;
        case "unit":
          row.rowData[netPriceFld].cellData = roundUpByUnit(
            row.rowData[netPriceFld].cellData,
          );
          break;
        case "tens":
          row.rowData[netPriceFld].cellData = roundUpByNumber(
            row.rowData[netPriceFld].cellData,
            10,
          );
          break;
        default:
          break;
      }
    });
  }
  return rowData;
};
